import React, { FormEvent, useState } from 'react';
import { tenant } from '../../../config/tenant';
import { whatsappLink } from '../whatsapp';

const SUBJECTS = [
	{ value: 'cadastro', label: 'Cadastro' },
	{ value: 'acesso', label: 'Acesso à Plataforma' },
	{ value: 'suporte', label: 'Suporte' },
	{ value: 'comercial', label: 'Informações Comerciais' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
	nome: string;
	email: string;
	assunto: string;
	mensagem: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = { nome: '', email: '', assunto: '', mensagem: '' };

/**
 * Formulário "Enviar solicitação" da home pública.
 *
 * A API consumida pela SPA não possui endpoint público de contato (o /Sistema
 * existente exige usuário autenticado). Até que exista um, a solicitação é
 * encaminhada ao WhatsApp comercial com os campos preenchidos.
 */
const HomeContactForm: React.FC = () => {
	const [form, setForm] = useState<FormState>(initialState);
	const [errors, setErrors] = useState<FormErrors>({});
	const [status, setStatus] = useState('');

	const update = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, [field]: e.target.value }));
		setErrors((prev) => ({ ...prev, [field]: undefined }));
		setStatus('');
	};

	const validate = (): boolean => {
		const next: FormErrors = {};
		if (!form.nome.trim()) next.nome = 'Informe seu nome.';
		if (!form.email.trim()) next.email = 'Informe seu e-mail.';
		else if (!EMAIL_RE.test(form.email.trim())) next.email = 'E-mail inválido.';
		if (!form.assunto) next.assunto = 'Selecione o assunto.';
		if (!form.mensagem.trim()) next.mensagem = 'Escreva sua mensagem.';
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!validate()) return;

		const subjectLabel = SUBJECTS.find((s) => s.value === form.assunto)?.label ?? form.assunto;
		const text = [
			`Olá! Solicitação enviada pelo site ${tenant.appName}.`,
			'',
			`Nome: ${form.nome.trim()}`,
			`E-mail: ${form.email.trim()}`,
			`Assunto: ${subjectLabel}`,
			'',
			form.mensagem.trim(),
		].join('\n');

		window.open(whatsappLink(text), '_blank', 'noopener,noreferrer');

		setForm(initialState);
		setStatus('Solicitação encaminhada ao WhatsApp da nossa equipe. Em breve entraremos em contato.');
	};

	return (
		<form className='cr-form' onSubmit={handleSubmit} noValidate aria-label='Enviar solicitação'>
			<div className='cr-form__row'>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='cr-contact-nome'>
						Nome
					</label>
					<input
						id='cr-contact-nome'
						className='cr-field'
						type='text'
						placeholder='Seu nome'
						autoComplete='name'
						maxLength={120}
						value={form.nome}
						onChange={update('nome')}
					/>
					{errors.nome && <span className='cr-error'>{errors.nome}</span>}
				</div>
				<div className='cr-group'>
					<label className='cr-label' htmlFor='cr-contact-email'>
						E-mail
					</label>
					<input
						id='cr-contact-email'
						className='cr-field'
						type='email'
						placeholder='seuemail@empresa.com.br'
						autoComplete='email'
						maxLength={160}
						value={form.email}
						onChange={update('email')}
					/>
					{errors.email && <span className='cr-error'>{errors.email}</span>}
				</div>
			</div>

			<div className='cr-group'>
				<label className='cr-label' htmlFor='cr-contact-assunto'>
					Assunto
				</label>
				<select id='cr-contact-assunto' className='cr-field' value={form.assunto} onChange={update('assunto')}>
					<option value=''>Selecione o assunto</option>
					{SUBJECTS.map((s) => (
						<option key={s.value} value={s.value}>
							{s.label}
						</option>
					))}
				</select>
				{errors.assunto && <span className='cr-error'>{errors.assunto}</span>}
			</div>

			<div className='cr-group'>
				<label className='cr-label' htmlFor='cr-contact-mensagem'>
					Mensagem
				</label>
				<textarea
					id='cr-contact-mensagem'
					className='cr-field cr-field--area'
					rows={3}
					placeholder='Digite sua mensagem'
					maxLength={2000}
					value={form.mensagem}
					onChange={update('mensagem')}
				/>
				{errors.mensagem && <span className='cr-error'>{errors.mensagem}</span>}
			</div>

			{status && (
				<p className='cr-success' role='status'>
					{status}
				</p>
			)}

			<button type='submit' className='cr-btn'>
				Enviar solicitação →
			</button>
		</form>
	);
};

export default HomeContactForm;
